import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  getSubscriptions,
  subscribePlan,
  getPlanConfig,
  validatePromotion,
  getCardConnectHost,
  getAllCountries,
  changeSubscriptionPaymentMethod,
  getUserActiveStats,
  validateUpdateSubscriptionPlan,
  updateSubscriptionPlan,
  getInvoiceHistory,
  subscriptionPlanPayment,
  getNewBillingInfo,
} from '../../services/subscription-service';
import {
  SubscribePlanRequest,
  SubscriptionPlansResponse,
  InvoiceHistoryListResponse,
  PlanConfigData,
  CountryData,
  ChangePaymentMethodRequest,
  ChangePaymentMethodResponse,
  UserActiveStatsResponse,
  ValidateUpdateSubscriptionPlanRequest,
  ValidateUpdateSubscriptionPlanResponse,
  UpdateSubscriptionPlanResponse,
  UpdateSubscriptionPlanRequest,
  ValidatePromotionResponse,
  ValidatePromotionRequest,
  SubscriptionPlanPaymentRequest,
  SubscriptionPlanPaymentResponse,
  SubscribePlanResponse,
  GetNewBillingInfoRequest,
  GetNewBillingInfoResponse,
} from '../../types';
import {
  getPlanConfigRequest,
  getPlanConfigSuccess,
  getPlanConfigFailure,
  getSubscriptionsFailure,
  getSubscriptionsRequest,
  getSubscriptionsSuccess,
  subscribePlanRequest,
  subscribePlanSuccess,
  subscribePlanFailure,
  validatePromotionRequest,
  validatePromotionSuccess,
  validatePromotionFailure,
  getCardConnectHostRequest,
  getCardConnectHostSuccess,
  getCardConnectHostFailure,
  getAllCountriesRequest,
  getAllCountriesSuccess,
  getAllCountriesFailure,
  changePaymentMethodRequest,
  changePaymentMethodSuccess,
  changePaymentMethodFailure,
  getUserActiveStatsFailure,
  getUserActiveStatsSuccess,
  getUserActiveStatsRequest,
  validateUpdateSubscriptionPlanSuccess,
  validateUpdateSubscriptionPlanFailure,
  validateUpdateSubscriptionPlanRequest,
  updateSubscriptionPlanRequest,
  updateSubscriptionPlanSuccess,
  updateSubscriptionPlanFailure,
  getInvoiceHistoryRequest,
  getInvoiceHistorySuccess,
  getInvoiceHistoryFailure,
  subscriptionPlanPaymentSuccess,
  subscriptionPlanPaymentFailure,
  subscriptionPlanPaymentRequest,
  getNewBillingInfoRequest,
  getNewBillingInfoSuccess,
  getNewBillingInfoFailure,
} from '@/modules/subscription/redux/slices/main-slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { ApiResponse } from '@/core/types';
import { showError } from '@/core/utils/toast-utils';
import {
  getDataSessionSuccess,
  refreshDataSessionRequest,
} from '@/modules/auth/redux/slices/main-slice';
import { InvoiceHistoryRequest } from '../../types'; // Invoice History
import { RootState } from '@/core/redux';
import { SUBSCRIPTION_STATUS } from '../../constants';
import {
  isNewBillingInfoApiSuccess,
  normalizeNewBillingInfoResponse,
} from '../../utils/suspended-due-payment-utils';

const selectAuthInfo = (state: RootState) => ({
  sessionData: state.auth.main.sessionData,
});

function* updateClientSubscriptionStatus(newStatus: string | null) {
  const { sessionData } = yield select(selectAuthInfo);
  if (
    sessionData &&
    sessionData.Client &&
    sessionData.Client.SubscriptionInfo &&
    sessionData.Client.SubscriptionInfo.Status !== newStatus
  ) {
    const updatedSessionData = {
      ...sessionData,
      Client: {
        ...sessionData.Client,
        SubscriptionInfo: {
          ...sessionData.Client.SubscriptionInfo,
          Status: newStatus,
        },
      },
    };
    // Persist to localStorage
    localStorage.setItem('clientSubscriptionStatus', newStatus || '');
    yield put(getDataSessionSuccess(updatedSessionData));
  }
}

/* #region Invoice History */
function* getInvoiceHistorySaga(action: PayloadAction<InvoiceHistoryRequest>) {
  try {
    const { page, limit } = action.payload;
    const res: ApiResponse<InvoiceHistoryListResponse> = yield call(
      getInvoiceHistory,
      page,
      limit
    );
    if (res.errorCode === 0 && res.data) {
      yield put(
        getInvoiceHistorySuccess({
          items: res.data.items ?? [],
          pagination: res.data.pagination ?? {
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: 10,
            hasNextPage: false,
            hasPrevPage: false,
            nextPage: null,
            prevPage: null,
          },
        })
      );
    } else {
      yield put(
        getInvoiceHistoryFailure(
          res?.message || 'Failed to fetch invoice history'
        )
      );
    }
  } catch (error: any) {
    yield put(
      getInvoiceHistoryFailure(
        error?.message || 'An error occurred while fetching invoice history'
      )
    );
    showError('Failed to fetch invoice history');
  }
}
/* #endregion Invoice History */

function* getPlanConfigSaga() {
  const response: ApiResponse<PlanConfigData> = yield call(getPlanConfig);
  if (response.data) {
    yield put(getPlanConfigSuccess(response.data));
  } else {
    yield put(getPlanConfigFailure('Failed to fetch plan config'));
  }
}

function* getSubscriptionsSaga(
  action: PayloadAction<
    | {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
      }
    | undefined
  >
) {
  const page = action?.payload?.page || 1;
  const limit = action?.payload?.limit || 1000;
  const sortBy = action?.payload?.sortBy || 'LastUpdatedAt';
  const sortOrder = action?.payload?.sortOrder || 'desc';
  const response: ApiResponse<SubscriptionPlansResponse> = yield call(
    getSubscriptions,
    page,
    limit,
    sortBy,
    sortOrder
  );

  if (response.errorCode === 0 && response.data) {
    yield put(getSubscriptionsSuccess(response.data));
  } else {
    yield put(getSubscriptionsFailure('Failed to fetch subscriptions'));
    showError('Failed to fetch subscriptions');
  }
}

function* subscribePlanSaga(action: PayloadAction<SubscribePlanRequest>) {
  const response: ApiResponse<SubscribePlanResponse> = yield call(
    subscribePlan,
    action.payload
  );

  console.log('response', response);

  if (response.errorCode === 0) {
    yield put(subscribePlanSuccess(response.data));
    // Refresh session to update payment method
    yield put(refreshDataSessionRequest());
  } else {
    // Handle specific error response: validate promotion error
    const context = response.context as {
      promotionValidation?: ValidatePromotionResponse;
    };
    if (context?.promotionValidation) {
      yield put(validatePromotionSuccess(context.promotionValidation));
    } else {
      yield put(
        subscribePlanFailure(response.message || 'Failed to subscribe plan')
      );
    }
  }
}

function* validatePromotionSaga(
  action: PayloadAction<ValidatePromotionRequest>
) {
  const response: ApiResponse<ValidatePromotionResponse> = yield call(
    validatePromotion,
    action.payload
  );

  if (response.data) {
    yield put(validatePromotionSuccess(response.data));
  } else {
    yield put(
      validatePromotionFailure(
        response.message || 'Failed to validate promotion'
      )
    );
  }
}

function* getCardConnectHostSaga() {
  const response: ApiResponse<string> = yield call(getCardConnectHost);
  if (response.data) {
    yield put(getCardConnectHostSuccess(response.data));
  } else {
    yield put(getCardConnectHostFailure('Failed to fetch CardConnect host'));
  }
}

function* getAllCountriesSaga() {
  const response: ApiResponse<CountryData[]> = yield call(getAllCountries);
  if (response.data) {
    yield put(getAllCountriesSuccess(response.data));
  } else {
    yield put(getAllCountriesFailure('Failed to fetch countries'));
  }
}

function* changePaymentMethodSaga(
  action: PayloadAction<ChangePaymentMethodRequest>
) {
  const response: ApiResponse<ChangePaymentMethodResponse> = yield call(
    changeSubscriptionPaymentMethod,
    action.payload
  );

  if (response.errorCode === 0 && response.data?.success) {
    yield put(changePaymentMethodSuccess());

    // Refresh session to update payment method
    yield put(refreshDataSessionRequest());
  } else {
    yield put(
      changePaymentMethodFailure(
        response.message || 'Failed to change payment method'
      )
    );
  }
}

function* getUserActiveStatsSaga() {
  const response: ApiResponse<UserActiveStatsResponse> =
    yield call(getUserActiveStats);
  if (response.data) {
    yield put(getUserActiveStatsSuccess(response.data));
  } else {
    yield put(
      getUserActiveStatsFailure(
        response.message || 'Failed to fetch user active stats'
      )
    );
  }
}

function* validateUpdateSubscriptionPlanSaga(
  action: PayloadAction<ValidateUpdateSubscriptionPlanRequest>
) {
  const response: ApiResponse<ValidateUpdateSubscriptionPlanResponse> =
    yield call(validateUpdateSubscriptionPlan, action.payload);
  if (response.errorCode == 0 && response.data) {
    yield put(validateUpdateSubscriptionPlanSuccess(response.data));
  } else {
    // Handle specific error response: validate promotion error
    console.log('validateUpdateSubscriptionPlanSaga response', response);
    if (response.context?.promotionValidation) {
      yield put(validatePromotionSuccess(response.context.promotionValidation));
    } else {
      yield put(
        validateUpdateSubscriptionPlanFailure(
          response.message || 'Failed to validate update subscription plan'
        )
      );
    }
  }
}

function* updateSubscriptionPlanSaga(
  action: PayloadAction<UpdateSubscriptionPlanRequest>
) {
  const response: ApiResponse<UpdateSubscriptionPlanResponse> = yield call(
    updateSubscriptionPlan,
    action.payload
  );

  if (response.errorCode === 0 && response.data) {
    const data = response.data;
    if (data && data.actionType == null && action.payload.actionType) {
      data.actionType = action.payload.actionType;
    }
    yield put(updateSubscriptionPlanSuccess(response.data));
    yield put(refreshDataSessionRequest());

    // update client subscription status in session and localStorage if changed
    yield call(
      updateClientSubscriptionStatus,
      response?.clientSubscriptionStatus || null
    );
  } else {
    // Handle specific error response: validate promotion error
    if (response.context?.promotionValidation) {
      yield put(validatePromotionSuccess(response.context.promotionValidation));
    } else {
      yield put(
        updateSubscriptionPlanFailure(
          response.message || 'Failed to update subscription plan'
        )
      );
    }
  }
}

function* subscriptionPlanPaymentSaga(
  action: PayloadAction<SubscriptionPlanPaymentRequest>
) {
  const response: ApiResponse<SubscriptionPlanPaymentResponse> = yield call(
    subscriptionPlanPayment,
    action.payload
  );

  if (response.errorCode === 0 && response.data) {
    yield put(subscriptionPlanPaymentSuccess(response.data));

    // refresh session to update subscription status
    if (action.payload.subscriptionStatus != SUBSCRIPTION_STATUS.SUSPENDED) {
      yield call(
        updateClientSubscriptionStatus,
        response?.clientSubscriptionStatus || null
      );
    }
  } else {
    yield put(
      subscriptionPlanPaymentFailure(
        response.data?.message || 'Failed to process subscription payment'
      )
    );
  }
}

function* getNewBillingInfoSaga(
  action: PayloadAction<GetNewBillingInfoRequest>
) {
  const response: ApiResponse<GetNewBillingInfoResponse> = yield call(
    getNewBillingInfo,
    action.payload
  );

  const rawPayload = response.data ?? response.context ?? response;
  const normalized =
    normalizeNewBillingInfoResponse(rawPayload) ??
    normalizeNewBillingInfoResponse(response);

  const isSuccess =
    (response.errorCode === 0 ||
      isNewBillingInfoApiSuccess(rawPayload) ||
      isNewBillingInfoApiSuccess(response)) &&
    normalized;

  if (isSuccess) {
    yield put(getNewBillingInfoSuccess(normalized));
  } else {
    yield put(
      getNewBillingInfoFailure(
        (response.data as { message?: string })?.message ||
          'Failed to load new billing info'
      )
    );
  }
}

export function* mainSaga() {
  console.log('subscription mainSaga started');
  yield takeLatest(getInvoiceHistoryRequest.type, getInvoiceHistorySaga);
  yield takeLatest(getPlanConfigRequest.type, getPlanConfigSaga);
  yield takeLatest(getSubscriptionsRequest.type, getSubscriptionsSaga);
  yield takeLatest(subscribePlanRequest.type, subscribePlanSaga);
  yield takeLatest(validatePromotionRequest.type, validatePromotionSaga);
  yield takeLatest(getCardConnectHostRequest.type, getCardConnectHostSaga);
  yield takeLatest(getAllCountriesRequest.type, getAllCountriesSaga);
  yield takeLatest(changePaymentMethodRequest.type, changePaymentMethodSaga);
  yield takeLatest(getUserActiveStatsRequest.type, getUserActiveStatsSaga);
  yield takeLatest(
    validateUpdateSubscriptionPlanRequest.type,
    validateUpdateSubscriptionPlanSaga
  );
  yield takeLatest(
    updateSubscriptionPlanRequest.type,
    updateSubscriptionPlanSaga
  );
  yield takeLatest(
    subscriptionPlanPaymentRequest.type,
    subscriptionPlanPaymentSaga
  );
  yield takeLatest(getNewBillingInfoRequest.type, getNewBillingInfoSaga);
}
